import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { getTodayEastern } from '../../lib/dateHelpers'

var BELL = String.fromCodePoint(0x1F514)

function timeAgo(dateStr) {
  var now = new Date()
  var d = new Date(dateStr)
  var diff = Math.floor((now - d) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago'
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago'
  if (diff < 604800) return Math.floor(diff / 86400) + 'd ago'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function NotificationBell() {
  var { user } = useAuth()
  var navigate = useNavigate()
  var [notifications, setNotifications] = useState([])
  var [unreadCount, setUnreadCount] = useState(0)
  var [showDropdown, setShowDropdown] = useState(false)
  var dropdownRef = useRef(null)

  var fetchNotifications = useCallback(async function() {
    if (!user) return
    var { data } = await supabase.from('notifications').select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20)
    setNotifications(data || [])
    setUnreadCount((data || []).filter(function(n) { return !n.is_read }).length)
  }, [user])

  useEffect(function() {
    fetchNotifications()
    var interval = setInterval(fetchNotifications, 30000)
    return function() { clearInterval(interval) }
  }, [fetchNotifications])

  // Also check for pending requests and overdue invoices
  useEffect(function() {
    if (!user) return
    var checkTriggers = async function() {
      // Check pending store requests (only for owners)
      var { data: pending } = await supabase.from('jobs').select('id, client_name, store_partner_id, created_at')
        .eq('request_status', 'pending')
        .eq('business_id', 'pools')
      if (pending && pending.length > 0) {
        for (var i = 0; i < pending.length; i++) {
          var job = pending[i]
          var { data: existing } = await supabase.from('notifications').select('id')
            .eq('related_id', job.id)
            .eq('type', 'store_request')
            .limit(1)
          if (!existing || existing.length === 0) {
            await supabase.from('notifications').insert({
              user_id: user.id,
              type: 'store_request',
              title: 'New Install Request',
              message: (job.client_name || 'A store partner') + ' requested a pool install.',
              link: '/store-requests?request=' + job.id,
              related_id: job.id,
              business_id: 'pools'
            })
          }
        }
      }

      // Check overdue invoices
      var today = getTodayEastern()
      var { data: overdue } = await supabase.from('invoices').select('id, client_name, invoice_number, due_date')
        .lt('due_date', today)
        .neq('status', 'paid')
        .neq('status', 'void')
      if (overdue && overdue.length > 0) {
        for (var j = 0; j < overdue.length; j++) {
          var inv = overdue[j]
          var { data: existingInv } = await supabase.from('notifications').select('id')
            .eq('related_id', inv.id)
            .eq('type', 'overdue_invoice')
            .limit(1)
          if (!existingInv || existingInv.length === 0) {
            await supabase.from('notifications').insert({
              user_id: user.id,
              type: 'overdue_invoice',
              title: 'Overdue Invoice',
              message: 'Invoice' + (inv.invoice_number ? ' #' + inv.invoice_number : '') + ' for ' + (inv.client_name || 'a client') + ' is past due.',
              link: '/invoicing?invoice=' + inv.id,
              related_id: inv.id,
              business_id: 'pools'
            })
          }
        }
      }

      fetchNotifications()
    }
    checkTriggers()
  }, [user, fetchNotifications])

  // Close dropdown on outside click
  useEffect(function() {
    var handleClick = function(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return function() { document.removeEventListener('mousedown', handleClick) }
  }, [])

  var markRead = async function(id) {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id)
    setNotifications(function(prev) { return prev.map(function(n) { return n.id === id ? { ...n, is_read: true } : n }) })
    setUnreadCount(function(prev) { return Math.max(0, prev - 1) })
  }

  var markAllRead = async function() {
    var unreadIds = notifications.filter(function(n) { return !n.is_read }).map(function(n) { return n.id })
    if (unreadIds.length === 0) return
    for (var i = 0; i < unreadIds.length; i++) {
      await supabase.from('notifications').update({ is_read: true }).eq('id', unreadIds[i])
    }
    setNotifications(function(prev) { return prev.map(function(n) { return { ...n, is_read: true } }) })
    setUnreadCount(0)
  }

  var handleClick = function(notif) {
    if (!notif.is_read) markRead(notif.id)
    if (notif.link) navigate(notif.link)
    setShowDropdown(false)
  }

  var typeIcons = {
    store_request: String.fromCodePoint(0x1F3EA),
    overdue_invoice: String.fromCodePoint(0x26A0),
    job_completed: String.fromCodePoint(0x2705)
  }

  return (
    <div ref={dropdownRef} style={s.wrapper}>
      <button onClick={function() { setShowDropdown(!showDropdown) }} style={s.bellBtn}>
        {BELL}
        {unreadCount > 0 && <span style={s.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>

      {showDropdown && (
        <div style={s.dropdown}>
          <div style={s.dropHeader}>
            <span style={s.dropTitle}>NOTIFICATIONS</span>
            {unreadCount > 0 && <button onClick={markAllRead} style={s.markAllBtn}>MARK ALL READ</button>}
          </div>
          {notifications.length === 0 && <div style={s.empty}>No notifications</div>}
          <div style={s.dropList}>
            {notifications.slice(0, 10).map(function(n) {
              return (
                <div key={n.id} onClick={function() { handleClick(n) }} style={{...s.notifItem, backgroundColor: n.is_read ? 'transparent' : 'rgba(180,140,60,0.06)'}}>
                  <span style={s.notifIcon}>{typeIcons[n.type] || BELL}</span>
                  <div style={{flex:1}}>
                    <div style={{...s.notifTitle, fontWeight: n.is_read ? 400 : 600}}>{n.title}</div>
                    <div style={s.notifMsg}>{n.message}</div>
                    <div style={s.notifTime}>{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.is_read && <span style={s.unreadDot} />}
                </div>
              )
            })}
          </div>
          <button onClick={function() { navigate('/notifications'); setShowDropdown(false) }} style={s.viewAllBtn}>VIEW ALL NOTIFICATIONS</button>
        </div>
      )}
    </div>
  )
}

var s = {
  wrapper:{position:'relative'},
  bellBtn:{background:'none',border:'none',fontSize:'20px',cursor:'pointer',position:'relative',padding:'4px 8px'},
  badge:{position:'absolute',top:'-2px',right:'0',backgroundColor:'#e05c5c',color:'#fff',fontSize:'10px',fontWeight:700,borderRadius:'10px',padding:'1px 5px',minWidth:'16px',textAlign:'center',fontFamily:"'Barlow Condensed',sans-serif"},
  dropdown:{position:'absolute',top:'100%',right:0,width:'340px',backgroundColor:'#0f1420',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',boxShadow:'0 8px 30px rgba(0,0,0,0.4)',zIndex:1000,overflow:'hidden'},
  dropHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 16px',borderBottom:'1px solid rgba(255,255,255,0.06)'},
  dropTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'2px',color:'rgba(255,255,255,0.5)'},
  markAllBtn:{background:'none',border:'none',color:'#4a8c6a',fontSize:'10px',fontFamily:"'Barlow Condensed',sans-serif",fontWeight:600,letterSpacing:'1px',cursor:'pointer'},
  dropList:{maxHeight:'350px',overflow:'auto'},
  notifItem:{display:'flex',gap:'10px',padding:'12px 16px',borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'pointer',alignItems:'flex-start'},
  notifIcon:{fontSize:'16px',marginTop:'2px'},
  notifTitle:{fontSize:'13px',color:'#ffffff'},
  notifMsg:{fontSize:'11px',color:'rgba(255,255,255,0.4)',marginTop:'2px'},
  notifTime:{fontSize:'10px',color:'rgba(255,255,255,0.2)',marginTop:'3px'},
  unreadDot:{width:'8px',height:'8px',borderRadius:'50%',backgroundColor:'#b48c3c',marginTop:'4px',flexShrink:0},
  empty:{padding:'24px',textAlign:'center',color:'rgba(255,255,255,0.2)',fontSize:'13px'},
  viewAllBtn:{width:'100%',padding:'10px',background:'none',border:'none',borderTop:'1px solid rgba(255,255,255,0.06)',color:'#b48c3c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
}
